// ✅ Importaciones principales
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Navegación y obtención de parámetros de la URL
import { ButtonSiguiente, Input } from '../../ui'; // Componentes personalizados de la UI
import { safeAxios } from '@/api/safeAxios';


// 🔹 Componente principal
const VerificaCodigo: React.FC = () => {
  // Hook para acceder a la URL actual (ej: /verificaCodigo?email=usuario@correo.com)
  const location = useLocation();

  // Hook de navegación programática (redirige al usuario)
  const navigate = useNavigate();

  // Extraemos parámetros de la URL
  const params = new URLSearchParams(location.search);

  // Obtenemos el email del query string o del localStorage (por si se perdió en la navegación)
  const email = params.get('email') || localStorage.getItem('resetEmail');

  // Estados locales
  const [codigo, setCodigo] = useState('');   // Código que introduce el usuario
  const [error, setError] = useState('');     // Mensaje de error (si el código es incorrecto)
  const [mensaje, setMensaje] = useState(''); // Mensaje de éxito (si el código es correcto)

  // 🔹 useEffect: se ejecuta al montar el componente
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Si no hay token guardado, mostramos un error
      setError('Token no encontrado. Intenta nuevamente.');
    }
  }, []);

  // 🔸 Función que se ejecuta al enviar el formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Previene recarga de la página
    setMensaje('');
    setError('');

    // Se obtiene el token almacenado en el navegador
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Token no encontrado. Intenta nuevamente.');
      return;
    }

    try {
      // 🔹 Llamada al backend para verificar el código
      const response = await safeAxios("/api/verificarCodigo", navigate, {
        method: "POST",
        data: { email, codigo, token },
        headers: { Authorization: `Bearer ${token}` }
      });

      // ✅ VALIDACIÓN ROBUSTA (no depende de "valido")
      const esValido =
        response.status === 200 ||
        response.data?.mensaje?.toLowerCase().includes("correcto") ||
        response.data?.success === true;

      if (esValido) {
        localStorage.setItem('resetEmail', email || '');
        localStorage.setItem('resetCode', codigo);

        setMensaje("Código correcto");

        setTimeout(() => {
          navigate('/introduceNuevaContrasena');
        }, 1000);

      } else {
        setError('Código incorrecto o expirado.');
      }

    } catch (err: any) {
      console.error('Error en la verificación:', err);

      if (!navigator.onLine) {
        // 🔴 Sin conexión → Offline
        navigate('/offline');

      } else if (err.response) {
        // 🔵 Error del servidor
        navigate('/errorPage');

      } else {
        // ⚪ Error genérico
        setError('Error al procesar la solicitud. Inténtalo de nuevo.');
      }
    }
  };

  // 🧱 Render del formulario
  return (
    <form
      className="flex flex-col justify-center items-center max-h-screen w-full pt-10 md:pt-20 px-6"
      onSubmit={handleSubmit}
    >
      <div className={`w-full sm:w-11/12 md:w-8/12 lg:w-4/12 xl:w-4/12 2xl:w-3/12 
                            p-10 space-y-4 border-2 rounded-lg border-r-4 border-t-4 
                            border-orange-600 shadow-md shadow-zinc-600`}>

        {/* Campo de entrada del código de verificación */}
        <div className="relative border-b border-white focus-within:border-white">
          <Input
            type="text"
            id="codigo"
            placeholder="Código de verificación"
            required
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            className="w-full outline-none py-2 focus:placeholder-transparent focus:border-green-600"
          />
        </div>

        {/* Mensajes dinámicos */}
        {mensaje && <p className="text-green-500 text-sm">{mensaje}</p>}
        {error && <p className="text-red-500 text-sm">{error}</p>}

        {/* Botón para verificar el código */}
        <div className="w-full">
          <ButtonSiguiente
            type="submit"
            className="w-full bg-black text-white hover:text-black font-bold hover:bg-white shadow-md shadow-white hover:shadow-blue-600"
          >
            Verificar
          </ButtonSiguiente>
        </div>
      </div>
    </form>
  );
};

// Exportamos el componente
export default VerificaCodigo;
